import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Cloud, User } from 'lucide-react';
import { Link } from 'react-router-dom';

interface WeatherData {
  current: {
    temp_c: number;
    condition: {
      text: string;
      icon: string;
    };
  };
  location: {
    name: string;
  };
}

export default function WeatherGreeting() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [greeting, setGreeting] = useState('');
  const [error, setError] = useState(false);
  const [location, setLocation] = useState<string>('');
  const { currentUser } = useAuth();

  // Immediately set the greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      setGreeting('Bom dia');
    } else if (hour >= 12 && hour < 20) {
      setGreeting('Boa tarde');
    } else {
      setGreeting('Boa noite');
    }
  }, []);

  // Handle weather updates
  useEffect(() => {
    const getWeather = async () => {
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              
              // Reverse geocoding para obter o nome da cidade
              try {
                const response = await fetch(
                  `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`
                );
                const data = await response.json();
                const locationName = data.city || data.locality || 'Localização Desconhecida';
                
                setWeather({
                  current: {
                    temp_c: 20,
                    condition: {
                      text: "Parcialmente nublado",
                      icon: "//cdn.weatherapi.com/weather/64x64/day/116.png"
                    }
                  },
                  location: {
                    name: locationName
                  }
                });
                setLocation(locationName);
              } catch (error) {
                console.error('Error getting location name:', error);
                // Fallback para localização padrão em caso de erro
                setDefaultWeather();
              }
            },
            (error) => {
              console.error('Error getting location:', error);
              // Fallback para localização padrão em caso de erro
              setDefaultWeather();
            }
          );
        } else {
          // Fallback para localização padrão se geolocalização não estiver disponível
          setDefaultWeather();
        }
      } catch (error) {
        console.error('Error:', error);
        setDefaultWeather();
      }
    };

    const setDefaultWeather = () => {
      setWeather({
        current: {
          temp_c: 20,
          condition: {
            text: "Parcialmente nublado",
            icon: "//cdn.weatherapi.com/weather/64x64/day/116.png"
          }
        },
        location: {
          name: "Localização Desconhecida"
        }
      });
      setLocation('Localização Desconhecida');
    };

    getWeather();

    // Update weather every 15 minutes
    const interval = setInterval(getWeather, 900000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="px-4 py-3 border-b border-[rgba(255,255,255,0.1)]">
      <div className="flex flex-col space-y-2">
        <div className="flex items-center space-x-3">
          <Link to="/profile" className="flex-shrink-0">
            {currentUser?.photoBase64 ? (
              <img
                src={currentUser.photoBase64}
                alt="Foto de perfil"
                className="h-8 w-8 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-[rgba(255,255,255,0.1)] flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
            )}
          </Link>
          <div className="flex flex-col">
            <span className="text-white font-medium">
              {currentUser?.displayName || 'User'}
            </span>
            <span className="text-gray-400 text-sm">{greeting}!</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 text-gray-400">
          <Cloud size={16} className="text-gray-400" />
          {weather ? (
            <>
              <span>{Math.round(weather.current.temp_c)}°C</span>
              <span>{location}</span>
            </>
          ) : (
            <span>A carregar temperatura...</span>
          )}
        </div>
      </div>
    </div>
  );
}
